<template>
  <div class="phone-input-wrapper">
    <v-row dense class="">
      <v-col cols="1">
        <v-select
          v-model="selectedCountry"
          :items="formattedCountries"
          item-text="label"
          item-value="code"
          return-object
          dense
          append-icon=""
        >
          <template v-slot:selection="{ item }">
            <div class="country-item">
              <img :src="item.flag" class="flag" />
              <span class="dial selected-dial">+{{ item.dialCode }}</span>
            </div>
          </template>

          <template v-slot:item="{ item }">
            <div class="country-item">
              <img :src="item.flag" class="flag" />
              <span class="name">{{ item.name }}</span>
              <span class="dial">+{{ item.dialCode }}</span>
            </div>
          </template>
        </v-select>
      </v-col>

      <v-col cols="11">
        <v-text-field
          v-model="localNumber"
          dense
          label="Phone"
          :rules="rules"
          @input="emitValue"
          maxlength="15"
          placeholder="Enter phone number"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import dialCodes from "@/utils/countryDialCodes";

export default {
  name: "PhoneInput",

  props: {
    value: {
      type: String,
      default: ""
    },
    countryId: {
      type: [Number, String],
      default: null
    },
    rules: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      selectedCountry: null,
      localNumber: "",
      internalDialCode: ""
    };
  },

  computed: {
    ...mapGetters("baseData", ["countries"]),

    formattedCountries() {
      return this.countries.map(c => {
        const dialCode = dialCodes[c.country_code] || "";

        return {
          id: c.id,
          name: c.country_name,
          code: c.country_code,
          dialCode,
          flag: `https://flagcdn.com/w40/${c.country_code.toLowerCase()}.png`,
          label: `${c.country_name} (+${dialCode})`
        };
      });
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.emitValue();
          return;
        }

        const match = val.match(/^\+(\d+)\s?(.*)$/);

        if (match) {
          const dial = match[1];
          const number = match[2];

          const found = this.formattedCountries.find(c => c.dialCode === dial);

          if (found) {
            this.selectedCountry = found;
            this.internalDialCode = dial;
          }

          this.localNumber = number;
        } else {
          this.localNumber = val;
        }
      }
    },

    countryId: {
      immediate: true,
      handler(id) {
        if (!id) return;

        const found = this.formattedCountries.find(c => c.id === id);
        if (found) {
          this.selectedCountry = found;
          this.internalDialCode = found.dialCode;

          this.emitValue();
        }
      }
    },

    selectedCountry(newVal) {
      if (!newVal) return;

      this.internalDialCode = newVal.dialCode;

      this.$emit("update:countryId", newVal.id);

      // 👇 reset number when switching country (optional behavior)
      this.localNumber = "";
      this.emitValue();
    }
  },

  methods: {
    emitValue() {
      this.localNumber = this.localNumber.replace(/[^\d ]/g, "");

      const fullValue = this.internalDialCode
        ? `+${this.internalDialCode} ${this.localNumber}`
        : this.localNumber;

      this.$emit("input", fullValue);
    }
  }
};
</script>

<style scoped>
.phone-input-wrapper {
  padding-top: 19px;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.flag {
  width: 18px;
  object-fit: cover;
  border-radius: 2px;
}

.name {
  flex: 1;
}

.dial {
  font-weight: 500;
  color: #555;
}

.selected-dial {
  right: 0;
  position: absolute;
}

/* Hide dial code on small screens */
@media (max-width: 600px) {
  .dial {
    display: none;
  }

  .v-select {
    min-width: 48px !important;
  }

  .country-item {
    justify-content: center;
  }
}

/* Medium screens */
@media (max-width: 960px) {
  .dial {
    font-size: 12px;
  }

  .flag {
    width: 12px !important;
  }
}
</style>
